import logo from "@/assets/logo_mivo.png";
import EmailVerificationBanner from "@/components/auth/EmailVerificationBanner";
import LegalReacceptModal from "@/components/auth/LegalReacceptModal";
import OfflineBanner from "@/components/offline/OfflineBanner";
import PlanAdjustmentNoticeBanner from "@/components/PlanAdjustmentNoticeBanner";
import {
    buildClinicManagerMobileNav,
    buildClinicManagerNav,
    clinicManagerProfessionalNavItem,
} from "@/domains/multi/clinicManagerNav";
import { canAccessMultiManagerNav, isMultiManagerRoute } from "@/domains/multi/clinicMemberPaths";
import { buildClinicSecretaryNav } from "@/domains/multi/clinicSecretaryNav";
import ClinicMigrationBanner from "@/domains/multi/components/ClinicMigrationBanner";
import ClinicWorkScheduleApproveDialog from "@/domains/multi/components/ClinicWorkScheduleApproveDialog";
import { Avatar, AvatarFallback } from "@/domains/shared/components/Avatar";
import { Badge } from "@/domains/shared/components/Badge";
import { Button } from "@/domains/shared/components/Button";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/domains/shared/components/Dialog";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { useUiStore } from "@/domains/shared/hooks/uiStore";
import { legalStatusQueryKey, useLegalStatus } from "@/domains/shared/hooks/useLegalStatus";
import { SESSION_AGENDA_UPDATED_EVENT, useSessionAgendaRealtime } from "@/domains/shared/hooks/useSessionAgendaRealtime";
import { useSessionRevocation } from "@/domains/shared/hooks/useSessionRevocation";
import { Roles } from "@/domains/shared/models/roles";
import { resolveNavAreaLabel } from "@/domains/shared/navigation/navAreaLabel";
import { buildSecretaryNav } from "@/domains/shared/navigation/secretaryNav";
import {
    buildTherapistMobileMoreNav,
    buildTherapistMobileNav,
    buildTherapistNav,
    therapistClinicManagementNavItem,
} from "@/domains/shared/navigation/therapistNav";
import { PARENT_NAV_TRANSLATIONS, APP_LAYOUT_TRANSLATIONS } from "@/domains/shared/navigation/constants";
import { authApi } from "@/domains/shared/apis/authApi";
import { LanguageChrome } from "@/domains/shared/components/LanguageChrome/LanguageChrome";
import { getTranslation, type MivoTranslationKey, useLocale } from "@mivo/i18n";
import { useTeamUnreadMessages } from "@/domains/shared/services/chat/useTeamUnreadMessages";
import { useUnreadMessages } from "@/domains/shared/services/chat/useUnreadMessages";
import { isEmailVerificationAllowedPath, needsEmailVerification } from "@/domains/shared/services/emailVerification";
import { initials } from "@/domains/shared/services/format";
import { queryClient } from "@/domains/shared/services/queryClient";
import { fetchAdminEmailUnreadSummary } from "@/domains/shared/apis/adminEmailInboxApi";
import { resolvePresenceModuleActive } from "@/domains/shared/services/secretaryClinicalPaths";
import { isClinicalAccessBlocked, isNavItemLockedForClinicalAccess } from "@/domains/subscription/accountAccess";
import { agendaMobileNavHref } from "@/domains/therapist/agendaNavigation";
import type { LucideIcon } from "lucide-react";
import {
    BookOpen,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    CreditCard,
    FileText,
    HelpCircle,
    ImageIcon,
    Inbox,
    LayoutDashboard,
    LogOut,
    Mail,
    Menu,
    MessageCircle,
    PersonStanding,
    Settings,
    Sparkles,
    Store,
    Target,
    Upload,
    User,
    Users,
    Wallet,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { cx } from "@/utils/cx";
import MivoWordMark from "./MivoWordMark";

type NavItem = {
    to: string;
    label: string;
    icon: LucideIcon;
    end?: boolean;
};

function buildParentNavItems(): NavItem[] {
    return [
        { to: "/familia", label: getTranslation(PARENT_NAV_TRANSLATIONS.home), icon: LayoutDashboard, end: true },
        { to: "/familia/agenda", label: getTranslation(PARENT_NAV_TRANSLATIONS.agenda), icon: CalendarDays },
        { to: "/familia/pessoas", label: getTranslation(PARENT_NAV_TRANSLATIONS.family), icon: PersonStanding },
        { to: "/familia/evolucao", label: getTranslation(PARENT_NAV_TRANSLATIONS.evolution), icon: Sparkles },
        { to: "/familia/objetivos", label: getTranslation(PARENT_NAV_TRANSLATIONS.goals), icon: Target },
        { to: "/familia/orientacoes", label: getTranslation(PARENT_NAV_TRANSLATIONS.guidance), icon: BookOpen },
        { to: "/familia/arquivos", label: getTranslation(PARENT_NAV_TRANSLATIONS.media), icon: ImageIcon },
        { to: "/familia/observacoes", label: getTranslation(PARENT_NAV_TRANSLATIONS.notes), icon: ClipboardList },
        { to: "/familia/atas-de-reuniao", label: getTranslation(PARENT_NAV_TRANSLATIONS.meetingMinutes), icon: FileText },
        { to: "/familia/solicitacoes", label: getTranslation(PARENT_NAV_TRANSLATIONS.requests), icon: HelpCircle },
        { to: "/familia/chat", label: getTranslation(PARENT_NAV_TRANSLATIONS.chat), icon: MessageCircle },
        { to: "/familia/financeiro", label: getTranslation(PARENT_NAV_TRANSLATIONS.finance), icon: Wallet },
        { to: "/familia/configuracoes", label: getTranslation(PARENT_NAV_TRANSLATIONS.settings), icon: Settings },
        { to: "/familia/perfil", label: getTranslation(PARENT_NAV_TRANSLATIONS.profile), icon: User },
    ];
}

function buildParentNav(hasFamilyFinanceAccess: boolean, hasFamilyMediaAccess: boolean): NavItem[] {
    return buildParentNavItems().filter((item) => {
        if (item.to === "/familia/financeiro") {
            return hasFamilyFinanceAccess;
        }
        if (item.to === "/familia/arquivos") {
            return hasFamilyMediaAccess;
        }
        return true;
    });
}

const ADMIN_NAV: NavItem[] = [
    { to: "/admin/terapeutas", label: "Terapeutas", icon: Users, end: true },
    { to: "/admin/responsaveis", label: "Responsáveis", icon: User },
    { to: "/admin/planos", label: "Planos", icon: CreditCard },
    { to: "/admin/emails", label: "E-mails", icon: Mail },
    { to: "/admin/email-inbox", label: "Caixa de e-mail", icon: Inbox },
    { to: "/admin/relatorios-ia", label: "Prompts de relatórios IA", icon: Sparkles },
    { to: "/admin/marketplace", label: "Marketplace", icon: Store },
    { to: "/admin/migracoes", label: "Migrações", icon: Upload },
    { to: "/admin/configuracoes", label: "Configurações", icon: Settings },
];

function mobileNavItems(nav: NavItem[], isTherapist: boolean, therapistMobileNav: NavItem[]): NavItem[] {
    return isTherapist ? therapistMobileNav : nav.slice(0, 5);
}

type SidebarNavLinkProps = {
    to: string;
    label: string;
    icon: LucideIcon;
    end?: boolean;
    locked: boolean;
    collapsed: boolean;
    unread: number;
    testId: string;
};

function SidebarNavLink({
    to,
    label,
    icon: Icon,
    end,
    locked,
    collapsed,
    unread,
    testId,
}: SidebarNavLinkProps) {
    const linkClassName = ({ isActive }: { isActive: boolean }) =>
        cx(
            "flex items-center text-sm font-semibold transition-all duration-200",
            collapsed ? "justify-center rounded-xl px-2 py-2" : "gap-3 rounded-lg px-3.5 py-2.5",
            isActive
                ? "bg-white/15 text-primary-foreground shadow-soft ring-1 ring-white/20"
                : "text-primary-foreground/90 hover:bg-white/10 hover:text-primary-foreground",
        );

    const unreadBadge =
        unread > 0 ? (
            collapsed ? (
                <span
                    aria-label={`${unread} ${getTranslation(APP_LAYOUT_TRANSLATIONS.unreadMessages)}`}
                    className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-accent ring-2 ring-primary"
                />
            ) : (
                <Badge className="min-w-5 h-5 shrink-0 border-0 bg-accent px-1.5 text-[10px] font-bold leading-none text-primary shadow-sm ring-1 ring-white/25 hover:bg-accent grid place-items-center">
                    {unread > 99 ? "99+" : unread}
                </Badge>
            )
        ) : null;

    const iconNode = (
        <span className="relative inline-flex shrink-0">
            <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            {collapsed ? unreadBadge : null}
        </span>
    );

    const content: ReactNode = locked ? (
        <button
            type="button"
            disabled
            aria-disabled="true"
            aria-label={label}
            title={collapsed ? label : undefined}
            data-testid={testId}
            className={cx(
                "flex w-full items-center text-left text-sm font-semibold text-primary-foreground/45 cursor-not-allowed select-none disabled:pointer-events-none disabled:opacity-100",
                collapsed ? "justify-center rounded-xl px-2 py-2" : "gap-3 rounded-lg px-3.5 py-2.5",
            )}
        >
            <Icon className="h-[18px] w-[18px] opacity-60" strokeWidth={2} />
            {!collapsed ? <span className="flex-1">{label}</span> : null}
        </button>
    ) : (
        <NavLink
            to={to}
            end={end}
            data-testid={testId}
            title={collapsed ? label : undefined}
            aria-label={collapsed ? label : undefined}
            className={linkClassName}
        >
            {iconNode}
            {!collapsed ? <span className="flex-1">{label}</span> : null}
            {!collapsed ? unreadBadge : null}
        </NavLink>
    );

    return content;
}

export default function AppLayout() {
    const { user, logout, refreshMe, applyAuthUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Subscribe to locale so getTranslation labels re-render without remounting the tree.
    useLocale();
    const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
    const isTherapist = user?.role === Roles.Therapist;
    const isSecretary = user?.role === Roles.Secretary;
    const clinicManager = canAccessMultiManagerNav(user);
    const isMultiManagerNav = isMultiManagerRoute(location.pathname);
    const refreshTimerRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isSecretary) {
            return;
        }

        const handleFocus = () => {
            if (refreshTimerRef.current != null) {
                window.clearTimeout(refreshTimerRef.current);
            }

            refreshTimerRef.current = window.setTimeout(() => {
                void refreshMe();
            }, 500);
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("focus", handleFocus);
            if (refreshTimerRef.current != null) {
                window.clearTimeout(refreshTimerRef.current);
            }
        };
    }, [isSecretary, refreshMe]);

    const isAdmin = user?.role === Roles.Admin;
    const showLanguageSwitcher = !isAdmin;
    const persistLocale = async (locale: MivoTranslationKey) => {
        if (!user) {
            return;
        }

        const { data } = await authApi.patchMe({ preferredLocale: locale });
        applyAuthUser(data.user);
    };
    const isPro = isTherapist;
    const therapistNav = buildTherapistNav(user, {
        activeClinicId: user?.activeClinicId,
    });
    const therapistMobileNav = buildTherapistMobileNav(user, {
        activeClinicId: user?.activeClinicId,
    });
    const therapistMobileMoreNav = buildTherapistMobileMoreNav(user, {
        activeClinicId: user?.activeClinicId,
    });
    const showTherapistClinicManagement = isPro && !isMultiManagerNav && clinicManager;
    const showProfessionalNavLink = isPro && isMultiManagerNav && clinicManager;
    const showTherapistMobileMoreMenu = isPro && !(isMultiManagerNav && clinicManager);
    const clinicManagerNav = buildClinicManagerNav(user);
    const clinicManagerMobileNav = buildClinicManagerMobileNav(user);
    const isClinicSecretary = isSecretary && user?.isClinicSecretary === true;
    const nav = isAdmin
        ? ADMIN_NAV
        : isClinicSecretary
            ? buildClinicSecretaryNav()
            : isSecretary
                ? buildSecretaryNav(user?.secretaryPermissions ?? null, {
                    secretaryAccessBlocked: user?.secretaryAccessBlocked === true,
                })
                : isPro && isMultiManagerNav && clinicManager
                    ? clinicManagerNav
                    : isPro
                        ? therapistNav
                        : buildParentNav(Boolean(user?.hasFamilyFinanceAccess), Boolean(user?.hasFamilyMediaAccess));
    const mobileNav = isAdmin
        ? ADMIN_NAV
        : isSecretary
            ? nav.slice(0, 5)
            : isPro && isMultiManagerNav && clinicManager
                ? clinicManagerMobileNav
                : mobileNavItems(nav, isTherapist, therapistMobileNav);
    const navAreaLabel = resolveNavAreaLabel(location.pathname, user);
    const parentAwaitingProfessional = user?.role === Roles.Parent && user?.hasLinkedProfessional === false;
    const emailUnverified = needsEmailVerification(user);
    const needsLegalCheck = user?.role === Roles.Therapist || user?.role === Roles.Parent;
    const { data: legalStatus } = useLegalStatus(Boolean(user && needsLegalCheck));
    const legalAcceptancePending = needsLegalCheck && Boolean(legalStatus?.requiresAcceptance);
    const { totalUnread: familyUnread } = useUnreadMessages(
        user?.role,
        user?.emailVerified,
        !isClinicalAccessBlocked(user),
    );
    const { totalUnread: teamUnread } = useTeamUnreadMessages(
        user?.role,
        user?.emailVerified,
        user,
        !isClinicalAccessBlocked(user),
    );
    const [adminInboxUnread, setAdminInboxUnread] = useState(0);
    const isAdminUser = user?.role === Roles.Admin;

    useEffect(() => {
        if (!isAdminUser) {
            return;
        }

        let cancelled = false;
        const load = async () => {
            try {
                const summary = await fetchAdminEmailUnreadSummary();
                if (!cancelled) {
                    setAdminInboxUnread(summary.totalUnread);
                }
            } catch {
                if (!cancelled) {
                    setAdminInboxUnread(0);
                }
            }
        };

        void load();
        const intervalId = window.setInterval(load, 60_000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [isAdminUser]);

    const navUnreadFor = (to: string) => {
        if (to === "/admin/email-inbox") {
            return adminInboxUnread;
        }
        if (to.endsWith("/chat-equipe")) {
            return teamUnread;
        }
        if (to.endsWith("/chat")) {
            return familyUnread;
        }
        return 0;
    };
    useSessionRevocation(user?.role, user?.emailVerified, !isClinicalAccessBlocked(user));
    const dispatchSessionAgendaUpdated = useCallback(() => {
        window.dispatchEvent(new CustomEvent(SESSION_AGENDA_UPDATED_EVENT));
    }, []);
    const presenceModuleActive = resolvePresenceModuleActive(user);
    useSessionAgendaRealtime(
        user?.role,
        user?.emailVerified,
        presenceModuleActive && !isClinicalAccessBlocked(user),
        dispatchSessionAgendaUpdated,
    );

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const sidebarFooterNavItems: NavItem[] = [
        ...(showTherapistClinicManagement ? [therapistClinicManagementNavItem()] : []),
        ...(showProfessionalNavLink ? [clinicManagerProfessionalNavItem()] : []),
    ];
    const therapistMobileMenuItems: NavItem[] = showTherapistMobileMoreMenu
        ? [
            ...therapistMobileMoreNav,
            ...(showTherapistClinicManagement ? [therapistClinicManagementNavItem()] : []),
        ]
        : [];

    const isSidebarFooterLinkLocked = (to: string) =>
        (emailUnverified && !isEmailVerificationAllowedPath(to))
        || legalAcceptancePending;

    const isNavLinkLocked = (to: string) =>
        (parentAwaitingProfessional && !isPro && to !== "/familia/perfil")
        || (emailUnverified && !isEmailVerificationAllowedPath(to))
        || legalAcceptancePending
        || isNavItemLockedForClinicalAccess(to, user);
    const sidebarOpen = useUiStore((state) => state.sidebarOpen);
    const toggleSidebar = useUiStore((state) => state.toggleSidebar);

    const renderMobileNavItem = ({ to, label, icon: Icon, end }: NavItem) => {
        const mobileTo = agendaMobileNavHref(to);
        const testId = `mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`;
        const locked = isNavLinkLocked(to);
        if (locked) {
            return (
                <button
                    key={to}
                    type="button"
                    disabled
                    aria-disabled="true"
                    aria-label={label}
                    data-testid={testId}
                    className="flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[9px] font-semibold text-primary-foreground/45 cursor-not-allowed select-none disabled:pointer-events-none disabled:opacity-100 sm:text-[10px]"
                >
                    <Icon className="h-5 w-5 opacity-50" strokeWidth={2} />
                    <span className="max-w-full truncate text-center">{label}</span>
                </button>
            );
        }
        return (
            <NavLink
                key={to}
                to={mobileTo}
                end={end}
                data-testid={testId}
                className={({ isActive }) =>
                    `flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1.5 rounded-lg text-[9px] font-semibold transition-colors sm:text-[10px] ${isActive ? "bg-white/15 text-primary-foreground ring-1 ring-white/20" : "text-primary-foreground/90"
                    }`
                }
            >
                <span className="relative">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                    {navUnreadFor(to) > 0 ? (
                        <span className="pointer-events-none absolute -top-1.5 -right-2 z-10 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold leading-none text-primary shadow-sm ring-2 ring-primary">
                            {navUnreadFor(to) > 9 ? "9+" : navUnreadFor(to)}
                        </span>
                    ) : null}
                </span>
                <span className="max-w-full truncate text-center">{label}</span>
            </NavLink>
        );
    };

    return (
        <div className="flex h-dvh min-h-0 overflow-hidden bg-background">
            {user && needsLegalCheck ? (
                <LegalReacceptModal
                    open={legalAcceptancePending}
                    user={user}
                    onAccepted={() => {
                        void queryClient.invalidateQueries({ queryKey: legalStatusQueryKey });
                    }}
                />
            ) : null}
            {user ? <ClinicWorkScheduleApproveDialog /> : null}
            {/* Sidebar */}
            <div className="hidden shrink-0 md:block">
                <aside
                    className={cx(
                        "sticky top-0 flex h-dvh min-h-0 flex-col border-r border-border bg-primary text-primary-foreground backdrop-blur-xl transition-[width] duration-300 ease-in-out",
                        sidebarOpen ? "w-72" : "w-[4.5rem]",
                    )}
                >
                    <div
                        className={cx(
                            "shrink-0 border-b border-white/10",
                            sidebarOpen ? "px-5 pt-6 pb-5" : "px-2 pt-4 pb-3",
                        )}
                    >
                        <div
                            className={cx(
                                "flex min-w-0",
                                sidebarOpen ? "items-start justify-between gap-3" : "flex-col items-center gap-2.5",
                            )}
                        >
                            <div className={cx("flex min-w-0 items-center", sidebarOpen ? "gap-2.5" : "justify-center")}>
                                <div
                                    className={cx(
                                        "shrink-0 rounded-xl bg-white p-1 shadow-soft ring-1 ring-white/20",
                                        sidebarOpen ? "p-1" : "p-0.5",
                                    )}
                                >
                                    <img src={logo} alt="Mivo" className={sidebarOpen ? "h-10 w-10" : "h-8 w-8"} />
                                </div>
                                {sidebarOpen ? (
                                    <div className="min-w-0">
                                        <MivoWordMark fontSize="20px" color="#FFFFFF" />
                                        <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground/90">{navAreaLabel}</p>
                                    </div>
                                ) : null}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={toggleSidebar}
                                data-testid="sidebar-toggle"
                                aria-label={sidebarOpen ? getTranslation(APP_LAYOUT_TRANSLATIONS.collapseMenu) : getTranslation(APP_LAYOUT_TRANSLATIONS.expandMenu)}
                                aria-expanded={sidebarOpen}
                                className={cx(
                                    "shrink-0 rounded-lg bg-white/10 text-primary-foreground/90 ring-1 ring-white/15 hover:bg-white/15 hover:text-primary-foreground aria-expanded:bg-white/10 aria-expanded:text-primary-foreground/90",
                                    sidebarOpen ? "h-8 w-8" : "h-7 w-7",
                                )}
                            >
                                {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>

                    <nav
                        className={cx(
                            "flex-1 space-y-1 overflow-y-auto py-3 [scrollbar-color:rgba(255,255,255,0.25)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20",
                            sidebarOpen ? "px-4" : "px-1.5",
                        )}
                    >
                        {nav.map(({ to, label, icon, end }) => {
                            const locked = isNavLinkLocked(to);
                            const testId = `nav-${label.toLowerCase().replace(/\s/g, "-")}`;

                            return (
                                <SidebarNavLink
                                    key={to}
                                    to={to}
                                    label={label}
                                    icon={icon}
                                    end={end}
                                    locked={locked}
                                    collapsed={!sidebarOpen}
                                    unread={navUnreadFor(to)}
                                    testId={testId}
                                />
                            );
                        })}
                    </nav>

                    {sidebarFooterNavItems.length > 0 ? (
                        <div className={cx("shrink-0 space-y-1 py-3 ", sidebarOpen ? "px-4" : "px-1.5")}>
                            {sidebarFooterNavItems.map(({ to, label, icon, end }) => {
                                const locked = isSidebarFooterLinkLocked(to);
                                const testId = `nav-${label.toLowerCase().replace(/\s/g, "-")}`;

                                return (
                                    <SidebarNavLink
                                        key={to}
                                        to={to}
                                        label={label}
                                        icon={icon}
                                        end={end}
                                        locked={locked}
                                        collapsed={!sidebarOpen}
                                        unread={0}
                                        testId={testId}
                                    />
                                );
                            })}
                        </div>
                    ) : null}

                    <div className={cx("shrink-0 border-t border-white/15 pb-4 pt-3", sidebarOpen ? "px-4" : "px-0")}>
                        <div
                            className={cx(
                                "rounded-xl bg-white/10 ring-1 ring-white/10",
                                sidebarOpen
                                    ? "flex items-center gap-3 px-3 py-2.5"
                                    : "mx-1.5 flex flex-col items-center justify-center gap-1.5 px-1.5 py-2",
                            )}
                        >
                            <Avatar className={cx("ring-2 ring-white/20", sidebarOpen ? "h-9 w-9" : "h-6 w-6")}>
                                <AvatarFallback className="bg-muted/30 text-primary-foreground text-sm font-bold backdrop-blur-sm">{initials(user?.name)}</AvatarFallback>
                            </Avatar>
                            {sidebarOpen ? (
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-bold text-white">{user?.name}</p>
                                    <p className="truncate text-xs text-white/70">{user?.email}</p>
                                </div>
                            ) : null}
                            {showLanguageSwitcher ? (
                                <LanguageChrome
                                    data-testid="shell-language-switcher"
                                    tone="onPrimary"
                                    onPersist={persistLocale}
                                    className={sidebarOpen ? "relative shrink-0" : "relative"}
                                />
                            ) : null}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleLogout}
                                data-testid="logout-btn"
                                className={cx(
                                    "rounded-lg text-primary-foreground/90 hover:bg-white/15 hover:text-primary-foreground",
                                    sidebarOpen ? "h-8 w-8" : "h-7 w-7",
                                )}
                                aria-label={getTranslation(APP_LAYOUT_TRANSLATIONS.logout)}
                                title={sidebarOpen ? undefined : getTranslation(APP_LAYOUT_TRANSLATIONS.logout)}
                            >
                                <LogOut className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </aside>
            </div>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-30 flex h-16 items-center justify-between border-b border-white/15 bg-primary px-4 shadow-sm">
                <div className="flex min-w-0 items-center gap-2.5">
                    <div className="shrink-0 rounded-xl bg-white p-1 shadow-soft ring-1 ring-white/20">
                        <img src={logo} alt="Mivo" className="h-8 w-8" />
                    </div>
                    <div className="flex min-w-0 flex-col">
                        <MivoWordMark fontSize="18px" color="#FFFFFF" />
                        <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-wider text-primary-foreground/70">{navAreaLabel}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    {showLanguageSwitcher ? (
                        <LanguageChrome
                            data-testid="shell-language-switcher-mobile"
                            tone="onPrimary"
                            onPersist={persistLocale}
                            className="relative shrink-0"
                        />
                    ) : null}
                    <Button variant="ghost" size="icon" onClick={handleLogout} className="h-9 w-9 rounded-lg text-primary-foreground/90 hover:text-primary-foreground hover:bg-white/15">
                        <LogOut className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Mobile bottom nav */}
            <div className="md:hidden fixed bottom-0 inset-x-0 z-30 flex items-center justify-around gap-0.5 border-t border-white/15 bg-primary px-1 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] shadow-[0_-4px_16px_-8px_rgb(0_0_0/0.25)]">
                {mobileNav.map(renderMobileNavItem)}
                {showTherapistMobileMoreMenu ? (
                    <button
                        type="button"
                        data-testid="mobile-nav-menu"
                        aria-label={getTranslation(APP_LAYOUT_TRANSLATIONS.moreOptions)}
                        aria-expanded={mobileMoreOpen}
                        aria-haspopup="dialog"
                        onClick={() => setMobileMoreOpen(true)}
                        className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1.5 rounded-lg text-[9px] font-semibold text-primary-foreground/90 transition-colors sm:text-[10px]"
                    >
                        <Menu className="h-5 w-5" strokeWidth={2} />
                        <span className="max-w-full truncate text-center">{getTranslation(APP_LAYOUT_TRANSLATIONS.menu)}</span>
                    </button>
                ) : null}
            </div>

            {showTherapistMobileMoreMenu ? (
                <Dialog open={mobileMoreOpen} onOpenChange={setMobileMoreOpen}>
                    <DialogContent
                        className="flex max-h-[min(92vh,960px)] w-[calc(100vw-2rem)] max-w-md flex-col gap-0 overflow-hidden rounded-2xl p-0"
                        data-testid="mobile-nav-more-dialog"
                    >
                        <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
                            <DialogTitle>{getTranslation(APP_LAYOUT_TRANSLATIONS.menu)}</DialogTitle>
                        </DialogHeader>
                        <DialogBody>
                        <nav className="flex flex-col gap-1" aria-label={getTranslation(APP_LAYOUT_TRANSLATIONS.moreNavOptions)}>
                            {therapistMobileMenuItems.map(({ to, label, icon: Icon, end }) => {
                                const mobileTo = agendaMobileNavHref(to);
                                const testId = `mobile-more-nav-${label.toLowerCase().replace(/\s/g, "-")}`;
                                const locked = isNavLinkLocked(to) || isSidebarFooterLinkLocked(to);
                                if (locked) {
                                    return (
                                        <button
                                            key={to}
                                            type="button"
                                            disabled
                                            aria-disabled="true"
                                            aria-label={label}
                                            data-testid={testId}
                                            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-semibold text-primary/40 cursor-not-allowed"
                                        >
                                            <Icon className="h-5 w-5 opacity-50" strokeWidth={2} />
                                            <span className="flex-1">{label}</span>
                                        </button>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={to}
                                        to={mobileTo}
                                        end={end}
                                        data-testid={testId}
                                        onClick={() => setMobileMoreOpen(false)}
                                        className={({ isActive }) =>
                                            cx(
                                                "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition-colors",
                                                isActive
                                                    ? "bg-primary/10 text-primary ring-1 ring-primary/20"
                                                    : "text-primary hover:bg-primary/5",
                                            )
                                        }
                                    >
                                        <span className="relative inline-flex shrink-0">
                                            <Icon className="h-5 w-5" strokeWidth={2} />
                                            {navUnreadFor(to) > 0 ? (
                                                <span className="pointer-events-none absolute -top-1.5 -right-2 z-10 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold leading-none text-primary shadow-sm ring-2 ring-white">
                                                    {navUnreadFor(to) > 9 ? "9+" : navUnreadFor(to)}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="flex-1">{label}</span>
                                    </NavLink>
                                );
                            })}
                        </nav>
                        </DialogBody>
                    </DialogContent>
                </Dialog>
            ) : null}

            <main className="flex min-h-0 w-0 min-w-0 flex-1 flex-col overflow-hidden pt-16 pb-[max(0.5rem,calc(3.75rem+env(safe-area-inset-bottom,0px)))] md:pb-0 md:pt-0">
                {parentAwaitingProfessional ? (
                    <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-3 md:px-8 text-sm text-primary" data-testid="parent-awaiting-professional-banner">
                        <p className="leading-relaxed">
                            <span className="font-semibold text-foreground">{getTranslation(APP_LAYOUT_TRANSLATIONS.parentBannerTitle)}</span>{" "}
                            {getTranslation(APP_LAYOUT_TRANSLATIONS.parentBannerBody)}{" "}
                            <Link to="/familia/perfil" className="font-bold text-secondary-foreground underline underline-offset-2 hover:text-secondary-foreground">
                                {getTranslation(APP_LAYOUT_TRANSLATIONS.parentBannerProfileLink)}
                            </Link>{" "}
                            {getTranslation(APP_LAYOUT_TRANSLATIONS.parentBannerSuffix)}
                        </p>
                    </div>
                ) : null}
                <EmailVerificationBanner />
                <PlanAdjustmentNoticeBanner />
                {isTherapist && !isMultiManagerNav ? <ClinicMigrationBanner /> : null}
                <OfflineBanner />
                <div className={cx("flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden", !isAdmin && "bg-muted/30")}>
                    {isAdmin ? (
                        <Outlet />
                    ) : (
                        <div className="mx-auto flex w-full max-w-[1400px] min-h-0 flex-1 flex-col">
                            <Outlet />
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
